import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import '../survey.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const GenericSurveyPage = () => {
    const { slug } = useParams();
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const STORAGE_KEY = `survey_draft_${slug}`;

    useEffect(() => {
        fetchSurvey();
    }, [slug]);

    // Load draft
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.answers) setAnswers(parsed.answers);
                if (parsed.step) setStep(parsed.step);
                if (parsed.submitted) setSubmitted(true);
            }
        } catch (e) { /* ignore */ }
    }, [slug]);

    // Auto-save draft
    useEffect(() => {
        if (!submitted) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, step, submitted }));
        }
    }, [answers, step, submitted]);

    const fetchSurvey = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/survey/public/${slug}`);
            if (res.data.success) {
                setSurvey(res.data.data.survey);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Survey not found');
        } finally { setLoading(false); }
    };

    const getQuestionsForStep = (stepNum) => {
        if (!survey?.questions) return [];
        return survey.questions.filter(q => q.step === stepNum);
    };

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
        if (errors[questionId]) setErrors(prev => ({ ...prev, [questionId]: null }));
    };

    const handleCheckbox = (questionId, option) => {
        setAnswers(prev => {
            const current = prev[questionId] || [];
            const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
            return { ...prev, [questionId]: updated };
        });
    };

    const validateStep = (stepNum) => {
        const questions = getQuestionsForStep(stepNum);
        const newErrors = {};
        questions.forEach(q => {
            if (q.required) {
                const val = answers[q.id];
                if (!val || (Array.isArray(val) && val.length === 0) || (typeof val === 'string' && !val.trim())) {
                    newErrors[q.id] = 'This field is required';
                }
            }
            // Check max selections
            if (q.maxSelections && Array.isArray(answers[q.id]) && answers[q.id].length > q.maxSelections) {
                newErrors[q.id] = `Please select a maximum of ${q.maxSelections} options`;
            }
            // Email validation
            if (q.type === 'email' && answers[q.id]) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(answers[q.id])) {
                    newErrors[q.id] = 'Please enter a valid email address';
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(s => Math.min(s + 1, survey.total_steps));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const prevStep = () => {
        setStep(s => Math.max(s - 1, 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        if (!validateStep(step)) return;
        try {
            setSubmitting(true);
            const res = await axios.post(`${API_URL}/survey/public/${survey.id}/submit`, {
                answers,
                email: answers.email || null
            });
            if (res.data.success) {
                toast.success(survey.thank_you_message || 'Thank you for completing the survey!');
                setSubmitted(true);
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, step, submitted: true }));
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to submit. Please try again.');
        } finally { setSubmitting(false); }
    };

    const progressPercent = submitted ? 100 : Math.round((step / (survey?.total_steps || 3)) * 100);

    // Render a single question
    const renderQuestion = (q) => (
        <div key={q.id} className="mb-4">
            <label className="form-label fw-semibold">
                {q.label} {q.required && <span className="text-danger">*</span>}
            </label>

            {q.type === 'text' && (
                <input type="text" className={`form-control ${errors[q.id] ? 'is-invalid' : ''}`}
                    value={answers[q.id] || ''} onChange={e => handleAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}

            {q.type === 'textarea' && (
                <textarea className={`form-control ${errors[q.id] ? 'is-invalid' : ''}`} rows={4}
                    value={answers[q.id] || ''} onChange={e => handleAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}

            {q.type === 'email' && (
                <input type="email" className={`form-control ${errors[q.id] ? 'is-invalid' : ''}`}
                    value={answers[q.id] || ''} onChange={e => handleAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}

            {q.type === 'number' && (
                <input type="number" className={`form-control ${errors[q.id] ? 'is-invalid' : ''}`}
                    value={answers[q.id] || ''} onChange={e => handleAnswer(q.id, e.target.value)} placeholder={q.placeholder || ''} />
            )}

            {q.type === 'select' && (
                <select className={`form-select ${errors[q.id] ? 'is-invalid' : ''}`} value={answers[q.id] || ''}
                    onChange={e => handleAnswer(q.id, e.target.value)}>
                    <option value="">-- Select --</option>
                    {(q.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            )}

            {q.type === 'radio' && (
                <div className={`d-flex flex-wrap gap-2 ${errors[q.id] ? 'border border-danger rounded p-2' : ''}`}>
                    {(q.options || []).map(opt => (
                        <button key={opt} type="button"
                            className={`btn btn-sm ${answers[q.id] === opt ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handleAnswer(q.id, opt)}
                            style={answers[q.id] === opt ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}>
                            {opt}
                        </button>
                    ))}
                </div>
            )}

            {q.type === 'checkbox' && (
                <div className={`row g-2 ${errors[q.id] ? 'border border-danger rounded p-2' : ''}`}>
                    {(q.options || []).map(opt => {
                        const selected = (answers[q.id] || []).includes(opt);
                        return (
                            <div key={opt} className="col-md-6">
                                <div className={`form-check p-2 rounded border ${selected ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}
                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                    onClick={() => handleCheckbox(q.id, opt)}>
                                    <input className="form-check-input" type="checkbox" checked={selected} onChange={() => handleCheckbox(q.id, opt)} />
                                    <label className="form-check-label small" style={{ cursor: 'pointer' }}>{opt}</label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {errors[q.id] && <div className="invalid-feedback d-block">{errors[q.id]}</div>}
        </div>
    );

    if (loading) {
        return (
            <PublicLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="text-center">
                        <i className="bi bi-exclamation-circle fs-1 text-danger d-block mb-3"></i>
                        <h3>{error}</h3>
                        <Link to="/" className="btn btn-outline-primary mt-3">Go Home</Link>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    // Thank You Page
    if (submitted) {
        return (
            <PublicLayout>
                <SEO title={`${survey.title} - Thank You`} description={survey.description || ''} image="/arca.survey.logo.jpg" url={`/survey/${slug}`} />
                <div className="py-5" style={{ background: 'linear-gradient(135deg, rgba(107,142,35,0.05) 0%, #ffffff 100%)', minHeight: '70vh' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8 text-center">
                                <div className="card border-0 shadow-sm"><div className="card-body p-5">
                                    <div className="mb-4">
                                        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#6B8E23', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="bi bi-check-lg text-white" style={{ fontSize: 40 }}></i>
                                        </div>
                                    </div>
                                    <h1 className="h2 fw-bold text-dark mb-3">Thank You!</h1>
                                    <p className="lead text-muted">
                                        {survey.thank_you_message || 'Your response has been recorded. Thank you for your time!'}
                                    </p>
                                    <hr className="my-4" />
                                    <div className="row g-3 mt-3">
                                        <div className="col-md-6"><Link to="/" className="btn btn-outline-primary w-100 py-3"><i className="bi bi-house me-2"></i>Home</Link></div>
                                        <div className="col-md-6"><Link to="/events" className="btn btn-outline-primary w-100 py-3"><i className="bi bi-calendar-event me-2"></i>Events</Link></div>
                                        <div className="col-md-6"><Link to="/membership_registration" className="btn btn-primary w-100 py-3"><i className="bi bi-person-plus me-2"></i>Join ARCA</Link></div>
                                        <div className="col-md-6">
                                            <button className="btn btn-outline-secondary w-100 py-3" onClick={() => {
                                                if (navigator.share) navigator.share({ title: survey.title, url: window.location.href });
                                                else { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
                                            }}><i className="bi bi-share me-2"></i>Share</button>
                                        </div>
                                    </div>
                                </div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <SEO
                title={`${survey.title} | ARCA Survey`}
                description={survey.description || 'Complete this survey to share your feedback.'}
                image="/arca.survey.logo.jpg"
                url={`/survey/${slug}`}
                structuredData={{
                    '@context': 'https://schema.org', '@type': 'Survey',
                    'name': survey.title, 'description': survey.description,
                    'provider': { '@type': 'Organization', 'name': 'Africa Respiratory Care Association', 'url': 'https://www.africaresp.org' }
                }}
            />
            <div className="py-4 py-md-5" style={{ background: 'linear-gradient(135deg, rgba(107,142,35,0.05) 0%, #ffffff 100%)', minHeight: '80vh' }}>
                <div className="container">
                    {/* Header */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-10 text-center">
                            <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #58761C 100%)' }}>
                                <div className="card-body p-4 text-white">
                                    <h1 className="h3 fw-bold mb-2">{survey.title}</h1>
                                    {survey.welcome_message && <p className="mb-0 opacity-90">{survey.welcome_message}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="row justify-content-center mb-4">
                        <div className="col-lg-10">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="small fw-medium">Step {step} of {survey.total_steps}</span>
                                <span className="small fw-medium">{progressPercent}% Complete</span>
                            </div>
                            <div className="progress" style={{ height: 8, borderRadius: 8 }}>
                                <div className="progress-bar" style={{ width: `${progressPercent}%`, backgroundColor: '#6B8E23', transition: 'width 0.4s ease', borderRadius: 8 }} />
                            </div>
                        </div>
                    </div>

                    {/* Questions */}
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="card arca-survey-card border-0 shadow-sm"><div className="card-body p-4 p-md-5">
                                {getQuestionsForStep(step).map(q => renderQuestion(q))}

                                {getQuestionsForStep(step).length === 0 && (
                                    <p className="text-muted text-center py-3">No questions in this step.</p>
                                )}

                                {/* Navigation */}
                                <div className="d-flex justify-content-between mt-5 pt-3 border-top">
                                    {step > 1 ? (
                                        <button className="btn btn-outline-secondary px-4" onClick={prevStep}><i className="bi bi-arrow-left me-2"></i>Previous</button>
                                    ) : <div />}

                                    {step < survey.total_steps ? (
                                        <button className="btn btn-primary px-4" onClick={nextStep} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                            Next<i className="bi bi-arrow-right ms-2"></i>
                                        </button>
                                    ) : (
                                        <button className="btn btn-primary px-5" onClick={handleSubmit} disabled={submitting} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                            {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Submitting...</> : <><i className="bi bi-send me-2"></i>Submit</>}
                                        </button>
                                    )}
                                </div>
                            </div></div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default GenericSurveyPage;
